import type { OrgSessionData } from '@strength/server-auth';
import { zql } from '../zero-schema.gen';

/**
 * Query builder for `table`, pre-scoped to the org. Module-private: it is the
 * one place that touches the raw `zql` builder, reached only through
 * `createOrgScopedQuery` below. Query authors never call it directly — they
 * receive the bound `orgQuery` builder from the define*Query procedures.
 *
 * @example withOrg('Block', ctx).where('trackId', 'IN', trackIds)
 */
const withOrg = <TTable extends keyof typeof zql>(
  table: TTable,
  ctx: OrgSessionData,
): (typeof zql)[TTable] =>
  (
    // SAFETY: TS can't prove 'orgId' is a valid column for the generic
    // TTable; cast through a minimal shape, the explicit return type
    // restores table-specific inference.
    zql[table] as unknown as {
      where(col: 'orgId', val: string): (typeof zql)[TTable];
    }
  ).where('orgId', ctx.orgId);

/**
 * The org-scoped query entrypoint handed to every query definition. It is
 * `withOrg` with the ctx already applied, so authors start a table query
 * without ever touching the raw `zql` builder. Mirrors tRPC's org-scoped
 * table-query API, `ctx.db.query.<Table>(orgId)`.
 */
export type OrgScopedQuery = <TTable extends keyof typeof zql>(
  table: TTable,
) => (typeof zql)[TTable];

/**
 * Binds `withOrg` to a session so query definitions receive an `orgQuery`
 * builder that always carries the orgId filter. Every query must start from
 * this, which is what makes an org-unscoped query impossible to write by
 * accident.
 */
export const createOrgScopedQuery =
  (ctx: OrgSessionData): OrgScopedQuery =>
  <TTable extends keyof typeof zql>(table: TTable) =>
    withOrg(table, ctx);
